/**
 * La **tuile d'application** : le carré coloré qui porte l'identité d'une app dans le dock et dans
 * les listes de l'espace enseignant.
 *
 * Ce fichier existe à cause d'un défaut signalé par l'enseignant le 7 septembre 2026 : « pourquoi
 * est-ce que le tableur et les icônes du CRM sont tous en gris au lieu d'avoir un design similaire
 * à des outils qui existent ». Deux causes, corrigées ensemble :
 *
 * 1. la couleur du manifeste ne se résolvait pas (`resolveTint` ne connaissait que les *noms* de
 *    paires, pas les valeurs hexadécimales que les manifestes écrivent) — corrigé dans `tokens.ts` ;
 * 2. rien, dans le contrat d'une app, ne lui permettait d'apporter sa **propre marque**. Le nom
 *    d'icône générique du manifeste (`grid`, `users`) puisait dans la trousse partagée du cœur :
 *    des formes volontairement neutres, réutilisables — donc sans identité.
 *
 * **Le mécanisme, et pourquoi celui-là.** Une app déclare dans son manifeste, en plus de sa
 * couleur, un `glyph` : une liste de tracés SVG sur la grille 24×24, dessinés en blanc sur sa
 * couleur. C'est de la **donnée**, pas du code — le manifeste est lu par le bureau, par l'API et
 * par le worker, il ne peut donc rien importer de React. Le cœur ne sait pas ce que le dessin
 * représente ; il le pose, c'est tout. Une app ajoutée demain apporte sa marque et sa couleur sans
 * qu'une ligne du cœur ne bouge — c'est le §52 du cahier des charges, appliqué au visuel.
 *
 * Une app qui ne déclare pas de `glyph` garde le comportement d'avant : son `icon` est cherché
 * dans la trousse du cœur. Rien ne casse, rien ne disparaît du dock.
 *
 * **Aucune couleur n'est inventée ici.** La tuile est peinte avec la paire de la palette verrouillée
 * (`docs/design/desktop-reference.md`) : l'encre porte le fond, et un éclat très léger vers la
 * teinte claire de la même paire donne le relief des icônes d'un vrai système. Le glyphe est en
 * `--surface` (#FFFFFF), la seule valeur claire de la référence.
 */
import type { CSSProperties, JSX } from 'react';
import { Icon } from './icons.js';
import { resolveTint, SURFACES } from './tokens.js';

/**
 * Une pièce du dessin d'une app, sur la grille 24×24 partagée par toute la trousse.
 *
 * Type **structurel** : `@ndrc/app-sdk` déclare la même forme de son côté, sans que l'un importe
 * l'autre. Le SDK ne doit rien savoir de React, et `@ndrc/ui` ne doit rien savoir du SDK.
 */
export interface GlyphShape {
  /** Le tracé (`<path d="…">`). */
  d: string;
  /** Rempli plutôt que tracé. Un aplat blanc porte mieux qu'un filet à 23 px. */
  fill?: boolean;
  /** Épaisseur du trait, quand 1,7 est trop lourd (les lignes internes d'une grille). */
  width?: number;
  /** Opacité, pour nuancer une pièce sans introduire une seconde couleur. */
  opacity?: number;
}

/** Ce que la tuile lit d'une app. Sous-ensemble structurel du manifeste : ni son id, ni son nom. */
export interface AppIdentity {
  /** Nom de forme dans la trousse du cœur, utilisé quand l'app ne dessine pas sa propre marque. */
  icon: string;
  /** Nom de paire (`'teal'`) ou valeur hexadécimale de la palette (`'#0F766E'`). Absente ou hors
   * palette : la paire neutre du bureau — ce que portent les tuiles du cœur, qui ne sont pas des
   * produits et n'ont donc pas de couleur à elles. */
  color?: string;
  /** La marque de l'app. Absente : `icon` est utilisé. */
  glyph?: readonly GlyphShape[];
}

/**
 * La peinture de la tuile — à poser sur l'élément que le cœur dessine déjà (le bouton du dock, la
 * pastille d'une liste), pour ne lui imposer ni balise ni géométrie. La taille, le rayon et les
 * transitions restent dans le CSS du cœur, où la référence les a fixés.
 */
export function appTileStyle(app: AppIdentity): CSSProperties {
  const pair = resolveTint(app.color);
  return {
    // Repli : un navigateur sans `color-mix` ignore la ligne suivante et garde l'aplat d'encre.
    backgroundColor: pair.ink,
    backgroundImage: `linear-gradient(152deg, color-mix(in oklab, ${pair.ink} 82%, ${pair.tint}) 0%, ${pair.ink} 64%)`,
    color: SURFACES.surface,
  };
}

export interface AppGlyphProps {
  app: AppIdentity;
  /** Côté du carré, en pixels. 23 dans le dock (référence), 20 dans les listes de l'enseignant. */
  size?: number;
}

/** La marque de l'app : son `glyph` si elle en déclare un, sinon la forme générique de `icon`. */
export function AppGlyph({ app, size = 23 }: AppGlyphProps): JSX.Element {
  const glyph = app.glyph;
  if (glyph === undefined || glyph.length === 0) {
    return <Icon name={app.icon} size={size} strokeWidth={1.7} />;
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {glyph.map((shape, index) => (
        <path
          key={index}
          d={shape.d}
          {...(shape.fill === true ? { fill: 'currentColor', stroke: 'none' } : {})}
          {...(shape.width === undefined ? {} : { strokeWidth: shape.width })}
          {...(shape.opacity === undefined ? {} : { opacity: shape.opacity })}
        />
      ))}
    </svg>
  );
}
